import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Check, ShieldCheck } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppInput } from '../components/ui/AppInput';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';
import { ApiError } from '../api/client';
import { loginWithCode, sendLoginCode } from '../api/auth';
import { loginWithPilotAccount } from '../api/internalTestAuth';
import { PILOT_FEATURE_ENABLED } from '../config/internalTestConfig';
import { markPrototypeLoggedIn } from '../utils/prototypeSession';
import { isValidPilotAccountId, normalizePilotAccountId } from '../utils/pilotAccountContract';

const CODE_COUNTDOWN_SECONDS = 30;
export const DEFAULT_LOGIN_PHONE = '';
export const DEFAULT_LOGIN_CODE = '';
type LoginMode = 'phone' | 'pilot';

const getLoginErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return fallback;
};

export default function LoginPage() {
  const router = useRouter();
  const [loginMode, setLoginMode] = useState<LoginMode>('phone');
  const [phone, setPhone] = useState(DEFAULT_LOGIN_PHONE);
  const [code, setCode] = useState(DEFAULT_LOGIN_CODE);
  const [agreed, setAgreed] = useState(true);
  const [pilotAccountId, setPilotAccountId] = useState('');
  const [pilotPassword, setPilotPassword] = useState('');
  const [pilotConsentAccepted, setPilotConsentAccepted] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCountdown((current) => Math.max(current - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const normalizedPhone = phone.trim();
  const canSubmitPhone = useMemo(
    () => normalizedPhone.length >= 11 && code.trim().length >= 4 && agreed,
    [agreed, code, normalizedPhone]
  );
  const canSubmitPilot = useMemo(
    () =>
      PILOT_FEATURE_ENABLED &&
      isValidPilotAccountId(pilotAccountId) &&
      pilotPassword.length >= 16 &&
      pilotConsentAccepted,
    [pilotAccountId, pilotConsentAccepted, pilotPassword]
  );

  const requestCode = useCallback(async () => {
    if (normalizedPhone.length < 11) {
      showAppToast('请输入 11 位手机号', 'info');
      return;
    }
    if (isRequestingCode || countdown > 0) {
      return;
    }
    setIsRequestingCode(true);
    setStatusMessage('');
    try {
      await sendLoginCode(normalizedPhone);
      setCountdown(CODE_COUNTDOWN_SECONDS);
      setStatusMessage('验证码已发送，请查收后输入。');
      showAppToast('验证码已发送', 'success');
    } catch (error) {
      const message = getLoginErrorMessage(error, '验证码发送失败，请稍后重试');
      setStatusMessage(message);
      showAppToast(message, 'error');
    } finally {
      setIsRequestingCode(false);
    }
  }, [countdown, isRequestingCode, normalizedPhone]);

  const submit = useCallback(async () => {
    if (loginMode === 'pilot') {
      if (!canSubmitPilot) {
        showAppToast('请填写内测账号、临时密码并确认匿名数据授权', 'info');
        return;
      }
      setIsSubmitting(true);
      setStatusMessage('');
      try {
        await loginWithPilotAccount({
          accountId: normalizePilotAccountId(pilotAccountId),
          temporaryPassword: pilotPassword,
          consentAccepted: pilotConsentAccepted,
        });
        await markPrototypeLoggedIn();
        showAppToast('内测登录成功', 'success');
        router.replace('/onboarding');
      } catch (error) {
        const message = getLoginErrorMessage(error, '内测登录失败，请检查临时凭据');
        setStatusMessage(message);
        showAppToast(message, 'error');
      } finally {
        setPilotPassword('');
        setIsSubmitting(false);
      }
      return;
    }

    if (!agreed) {
      showAppToast('请先同意用户协议和隐私政策', 'info');
      return;
    }
    if (!canSubmitPhone) {
      showAppToast('请补全手机号和验证码', 'info');
      return;
    }
    setIsSubmitting(true);
    setStatusMessage('');
    try {
      await loginWithCode({ phone: normalizedPhone, code: code.trim() });
      await markPrototypeLoggedIn();
      showAppToast('登录成功', 'success');
      router.replace('/onboarding');
    } catch (error) {
      const message = getLoginErrorMessage(error, '登录失败，请稍后重试');
      setStatusMessage(message);
      showAppToast(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    agreed,
    canSubmitPhone,
    canSubmitPilot,
    code,
    loginMode,
    normalizedPhone,
    pilotAccountId,
    pilotConsentAccepted,
    pilotPassword,
    router,
  ]);

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="返回"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <View style={styles.heroIcon}>
            <ShieldCheck size={28} color={tokens.colors.white} />
          </View>
          <AppText variant="title" weight="bold">
            登录
          </AppText>
          <AppText variant="body" tone="muted" align="center">
            {loginMode === 'pilot' ? '使用受控内测账号进入 staging 学习空间' : '使用手机号和验证码进入登峰学习空间'}
          </AppText>
        </View>

        <AppCard style={styles.formCard}>
          {PILOT_FEATURE_ENABLED ? (
            <View style={styles.modeSwitch} accessibilityRole="tablist">
              {(['phone', 'pilot'] as const).map((mode) => {
                const selected = loginMode === mode;
                return (
                  <Pressable
                    key={mode}
                    accessibilityRole="tab"
                    accessibilityState={{ selected }}
                    onPress={() => {
                      setLoginMode(mode);
                      setStatusMessage('');
                    }}
                    style={[styles.modeOption, selected ? styles.modeOptionSelected : null]}
                  >
                    <AppText variant="body" weight="semibold" tone={selected ? 'primary' : 'muted'}>
                      {mode === 'phone' ? '验证码登录' : '账号密码'}
                    </AppText>
                  </Pressable>
                );
              })}
            </View>
          ) : null}

          {loginMode === 'pilot' ? (
            <>
              <View style={styles.field}>
                <AppText variant="body" weight="bold">
                  内测账号
                </AppText>
                <AppInput
                  value={pilotAccountId}
                  onChangeText={setPilotAccountId}
                  autoCapitalize="none"
                  autoCorrect={false}
                  placeholder="请输入受控内测账号"
                />
              </View>

              <View style={styles.field}>
                <AppText variant="body" weight="bold">
                  一次性临时密码
                </AppText>
                <AppInput
                  value={pilotPassword}
                  onChangeText={setPilotPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                  secureTextEntry
                  placeholder="请输入一次性临时密码"
                />
              </View>

              <Pressable
                accessibilityRole="checkbox"
                accessibilityState={{ checked: pilotConsentAccepted }}
                onPress={() => setPilotConsentAccepted((current) => !current)}
                style={styles.agreementRow}
              >
                <View style={[styles.checkbox, pilotConsentAccepted ? styles.checkboxChecked : null]}>
                  {pilotConsentAccepted ? <Check size={14} color={tokens.colors.white} /> : null}
                </View>
                <AppText variant="caption" tone="muted" style={styles.agreementCopy}>
                  同意内测期间记录匿名行为数据：事件编号、时间、应用版本和粗粒度设备类型；不记录题目、图片、手机号、密码或访问令牌。退出后停止记录并清理待发送队列。
                </AppText>
              </Pressable>
            </>
          ) : (
            <>
              <View style={styles.field}>
                <AppText variant="body" weight="bold">
                  手机号
                </AppText>
                <AppInput
                  value={phone}
                  onChangeText={setPhone}
                  placeholder="请输入手机号"
                  keyboardType="phone-pad"
                  maxLength={11}
                />
              </View>

              <View style={styles.field}>
                <AppText variant="body" weight="bold">
                  验证码
                </AppText>
                <View style={styles.codeRow}>
                  <AppInput
                    value={code}
                    onChangeText={setCode}
                    placeholder="请输入验证码"
                    keyboardType="number-pad"
                    maxLength={6}
                    style={styles.codeInput}
                  />
                  <AppButton
                    label={countdown > 0 ? `${countdown}s` : '获取验证码'}
                    variant="secondary"
                    disabled={countdown > 0 || isRequestingCode}
                    loading={isRequestingCode}
                    onPress={requestCode}
                    style={styles.codeButton}
                  />
                </View>
              </View>

              <Pressable
                accessibilityRole="checkbox"
                accessibilityState={{ checked: agreed }}
                onPress={() => setAgreed((current) => !current)}
                style={styles.agreementRow}
              >
                <View style={[styles.checkbox, agreed ? styles.checkboxChecked : null]}>
                  {agreed ? <Check size={14} color={tokens.colors.white} /> : null}
                </View>
                <AppText variant="caption" tone="muted" style={styles.agreementCopy}>
                  登录即表示同意 用户协议 和 隐私政策
                </AppText>
              </Pressable>
            </>
          )}

          {statusMessage ? (
            <AppText variant="caption" tone="muted">
              {statusMessage}
            </AppText>
          ) : null}

          <AppButton
            label={loginMode === 'pilot' ? '进入内测' : '登录'}
            disabled={(loginMode === 'pilot' ? !canSubmitPilot : !canSubmitPhone) || isSubmitting}
            loading={isSubmitting}
            onPress={submit}
          />
        </AppCard>

        <AppCard style={styles.noticeCard}>
          <AppText variant="subtitle" weight="bold">
            {loginMode === 'pilot' ? '受控 staging 内测' : '真实验证码登录'}
          </AppText>
          <AppText variant="body" tone="muted">
            {loginMode === 'pilot'
            ? '临时密码只用于本次请求且不会保存；访问令牌在移动端进入系统安全存储，在 Web 端仅保留于当前浏览器标签会话。'
            : '获取验证码会调用当前 staging 登录服务；访问令牌在移动端进入系统安全存储，在 Web 端仅保留于当前浏览器标签会话。'}
          </AppText>
          <AppText variant="subtitle" weight="bold">
            会话保护
          </AppText>
          <AppText variant="body" tone="muted">
            失效或无效令牌会清除本机会话并回到登录页；重启后会从安全存储恢复登录态。
          </AppText>
        </AppCard>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
  },
  backButton: {
    alignSelf: 'flex-start',
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.xl,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  hero: {
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  heroIcon: {
    width: 58,
    height: 58,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.primary,
  },
  formCard: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    gap: tokens.spacing.lg,
  },
  modeSwitch: {
    minHeight: 44,
    flexDirection: 'row',
    borderRadius: tokens.radius.md,
    backgroundColor: tokens.colors.surfaceMuted,
    padding: tokens.spacing.xs,
  },
  modeOption: {
    flex: 1,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.radius.sm,
  },
  modeOptionSelected: {
    backgroundColor: tokens.colors.surface,
  },
  field: {
    gap: tokens.spacing.sm,
  },
  codeRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
  },
  codeInput: {
    flex: 1,
    minWidth: 0,
  },
  codeButton: {
    minWidth: 118,
    paddingHorizontal: tokens.spacing.md,
  },
  agreementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: tokens.radius.sm,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  checkboxChecked: {
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.primary,
  },
  agreementCopy: {
    flex: 1,
  },
  noticeCard: {
    gap: tokens.spacing.sm,
    backgroundColor: tokens.colors.surfaceMuted,
  },
});
